// packages/web-ui/src/AppShell/drawer-tabs/DebugTab.tsx
//
// W2-S9 — Debug tab body. Health snapshot + event-log summary + build
// version (UX §7.6).
//
// Migration final fix-pass — HIGH-3. Replaces the static italic placeholder.
// Reads:
//   - Capability snapshot (`hasGit`, `hasKbConfig`, `hasDomainAgents`,
//     `hasSubAgents`, `workspaceRoot`) via the existing `useCapabilities()`
//     hook. The `capabilities` slice is owned by Agent B in this fix-pass —
//     this file only CONSUMES it; no slice mutations.
//   - Event-log summary (`total`, `bySource`) via the eventLog slice.
//   - Build version from `import.meta.env` (`VITE_TESSA_BUILD_VERSION` /
//     `MODE`).
//
// Out-of-scope: re-scan controls, recent-errors list, audit-log path hint.
// These belong to the W6 settings panel + a future diagnostics route.

import { useMemo } from 'react';
import type { JSX } from 'react';
import type { StoreApi } from 'zustand/vanilla';
import { useRootStore } from '../../state/store.js';
import { selectEventLogSummary } from '../../state/eventLog.js';
import { useCapabilities } from '../../hooks/useCapabilities.js';
import type { CapabilitiesSlice } from '../../state/capabilities.js';

export interface DebugTabProps {
  /**
   * Test-only injection — replaces the singleton capabilities store. When
   * omitted the hook falls back to the shared singleton (production path).
   */
  capabilitiesStore?: StoreApi<CapabilitiesSlice>;
}

interface BuildInfo {
  version: string;
  mode: string;
}

function readBuildInfo(): BuildInfo {
  // `import.meta.env` is the Vite-injected build-time bag. The runtime cast
  // accommodates both production and the test environment, where Vitest's
  // runtime supplies a `MODE` of `"test"`.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  return {
    version: env.VITE_TESSA_BUILD_VERSION ?? 'dev',
    mode: env.MODE ?? 'unknown',
  };
}

export function DebugTab({ capabilitiesStore }: DebugTabProps = {}): JSX.Element {
  const { capabilities, loading, error } = useCapabilities(
    capabilitiesStore !== undefined ? { store: capabilitiesStore, poll: false } : {},
  );
  const entries = useRootStore((s) => s.entries);
  const summary = useMemo(() => selectEventLogSummary({ entries }), [entries]);
  const build = useMemo(() => readBuildInfo(), []);

  return (
    <div
      id="drawer-panel-debug"
      role="tabpanel"
      aria-labelledby="drawer-tab-debug"
      data-testid="drawer-panel-debug"
      className="flex flex-1 flex-col overflow-hidden p-3 text-xs"
    >
      <section data-testid="debug-build-section" className="mb-4">
        <p className="mb-1 font-semibold uppercase tracking-wide text-taupe-700">
          Build
        </p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-taupe-900">
          <dt className="text-taupe-600">Version</dt>
          <dd data-testid="debug-build-version">{build.version}</dd>
          <dt className="text-taupe-600">Mode</dt>
          <dd data-testid="debug-build-mode">{build.mode}</dd>
        </dl>
      </section>

      <section data-testid="debug-capabilities-section" className="mb-4">
        <p className="mb-1 font-semibold uppercase tracking-wide text-taupe-700">
          Capabilities
        </p>
        {loading && capabilities === null ? (
          <p data-testid="debug-capabilities-loading" className="italic text-taupe-600">
            Polling /api/v1/health…
          </p>
        ) : error !== null && capabilities === null ? (
          <p data-testid="debug-capabilities-error" className="text-audi-red">
            Health probe error: {error}
          </p>
        ) : capabilities === null ? (
          <p data-testid="debug-capabilities-unknown" className="italic text-taupe-600">
            Capability snapshot not yet available.
          </p>
        ) : (
          <dl
            className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-taupe-900"
            data-testid="debug-capabilities-grid"
          >
            <dt className="text-taupe-600">Workspace</dt>
            <dd
              data-testid="debug-capabilities-workspace"
              className="break-all font-mono text-[0.65rem]"
            >
              {capabilities.workspaceRoot ?? '(none)'}
            </dd>
            <dt className="text-taupe-600">hasGit</dt>
            <dd data-testid="debug-capabilities-hasGit">
              {capabilities.hasGit ? 'yes' : 'no'}
            </dd>
            <dt className="text-taupe-600">hasKbConfig</dt>
            <dd data-testid="debug-capabilities-hasKbConfig">
              {capabilities.hasKbConfig ? 'yes' : 'no'}
            </dd>
            <dt className="text-taupe-600">hasDomainAgents</dt>
            <dd data-testid="debug-capabilities-hasDomainAgents">
              {capabilities.hasDomainAgents ? 'yes' : 'no'}
            </dd>
            <dt className="text-taupe-600">hasSubAgents</dt>
            <dd data-testid="debug-capabilities-hasSubAgents">
              {capabilities.hasSubAgents ? 'yes' : 'no'}
            </dd>
          </dl>
        )}
      </section>

      <section data-testid="debug-eventlog-section" className="mb-4">
        <p className="mb-1 font-semibold uppercase tracking-wide text-taupe-700">
          Recent activity
        </p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-taupe-900">
          <dt className="text-taupe-600">Total</dt>
          <dd data-testid="debug-eventlog-total">{summary.total}</dd>
          <dt className="text-taupe-600">By source</dt>
          <dd data-testid="debug-eventlog-by-source" className="font-mono text-[0.65rem]">
            {Object.entries(summary.bySource)
              .filter(([, v]) => v > 0)
              .map(([k, v]) => `${k}=${v}`)
              .join(' ') || '(none)'}
          </dd>
        </dl>
      </section>

      <p className="mt-auto text-[0.6rem] italic text-taupe-500" data-testid="debug-footer-note">
        Re-scan controls and audit-log export land in the W6 settings panel.
      </p>
    </div>
  );
}
